// Utility functions for determining user type from URL and context

/**
 * Extracts user type from URL pathname
 * Returns 'sender', 'traveller', or 'generic' based on the URL path
 * Returns null if user type cannot be determined from URL
 */
export const getUserTypeFromPath = (pathname: string): 'sender' | 'traveller' | 'generic' | null => {
  const path = pathname.toLowerCase();

  // Check if path explicitly contains sender or traveller
  if (path.includes('/sender')) {
    return 'sender';
  }

  if (path.includes('/traveller') || path.includes('/traveller')) {
    return 'traveller';
  }

  // Root path defaults to generic (unauthorized landing page)
  if (path === '/' || path === '/support') {
    return 'generic';
  }

  // Cannot determine from path
  return null;
};

/**
 * Validates if a string is a valid user type
 */
export const isValidUserType = (value: unknown): value is 'sender' | 'traveller' | 'generic' => {
  return value === 'sender' || value === 'traveller' || value === 'generic';
};

/**
 * Gets user type from navigation state or URL, with fallback
 * Priority: state.themeRole > state.userRole > pathname > default (null)
 */
export const getUserTypeFromContext = (
  pathname: string,
  state?: { userRole?: string; themeRole?: string } | null
): 'sender' | 'traveller' | 'generic' | null => {
  // First, check for themeRole in state (for UI theme override)
  if (state?.themeRole && isValidUserType(state.themeRole)) {
    return state.themeRole;
  }

  // Then check navigation state userRole
  if (state?.userRole && isValidUserType(state.userRole)) {
    return state.userRole;
  }

  // Fallback to URL path
  return getUserTypeFromPath(pathname);
};
